import { LOCAL } from "@mn/shared";
import { useRef } from "react";
import * as Y from "yjs";
import { useT } from "@/lib/i18n";
import { useBoard } from "../BoardContext";
import { useDeepVersion } from "../hooks";
import { useBoardUI } from "../store";

interface Item {
  id: string;
  text: string;
  done: boolean;
  indent: number;
}

/** To-do list: items live in a Y.Array of Y.Map so concurrent adds merge; each item's text is last-writer-wins. */
export function TodoBody({ id, ymap }: { id: string; ymap: Y.Map<unknown> }) {
  const { handle, canEdit } = useBoard();
  const t = useT();
  const items = ymap.get("items") as Y.Array<Y.Map<unknown>>;
  const title = (ymap.get("title") as string) ?? "";
  const selected = useBoardUI((s) => s.selection.has(id));
  const version = useDeepVersion(items);
  const focusNext = useRef<string | null>(null);
  const tx = (fn: () => void) => handle.doc.transact(fn, LOCAL);
  const list: Item[] = items.toArray().map((m) => ({
    id: m.get("id") as string,
    text: (m.get("text") as string) ?? "",
    done: !!m.get("done"),
    indent: (m.get("indent") as number) ?? 0,
  }));

  const add = (afterIndex: number, text = "") => {
    const m = new Y.Map<unknown>();
    const nid = crypto.randomUUID();
    tx(() => {
      items.insert(afterIndex + 1, [m]);
      m.set("id", nid);
      m.set("text", text);
      m.set("done", false);
      m.set("indent", list[afterIndex]?.indent ?? 0);
    });
    focusNext.current = nid;
    queueMicrotask(() =>
      (document.querySelector(`[data-todo="${nid}"]`) as HTMLInputElement | null)?.focus(),
    );
  };
  const update = (i: number, patch: Partial<Item>) =>
    tx(() => {
      const m = items.get(i);
      for (const [k, v] of Object.entries(patch)) m.set(k, v);
    });
  const remove = (i: number) => tx(() => items.delete(i, 1));

  return (
    <div className="px-3 py-2" data-v={version}>
      <input
        value={title}
        readOnly={!canEdit}
        placeholder={t.todoTitle}
        onChange={(e) => tx(() => ymap.set("title", e.target.value))}
        onPointerDown={(e) => selected && e.stopPropagation()}
        className="mb-1 w-full bg-transparent text-[14px] font-semibold outline-none placeholder:text-dim"
      />
      {list.map((it, i) => (
        <div key={it.id} className="flex items-start gap-2 py-0.5" style={{ paddingLeft: it.indent * 18 }}>
          <input
            type="checkbox"
            checked={it.done}
            disabled={!canEdit}
            onChange={(e) => update(i, { done: e.target.checked })}
            onPointerDown={(e) => e.stopPropagation()}
            className="mt-1 size-3.5 accent-white"
          />
          <input
            data-todo={it.id}
            value={it.text}
            readOnly={!canEdit}
            placeholder={t.todoItem}
            onChange={(e) => update(i, { text: e.target.value })}
            onPointerDown={(e) => selected && e.stopPropagation()}
            onKeyDown={(e) => {
              if (!canEdit) return;
              if (e.key === "Enter") {
                e.preventDefault();
                add(i);
              } else if (e.key === "Backspace" && it.text === "" && list.length > 1) {
                e.preventDefault();
                remove(i);
                (
                  document.querySelector(`[data-todo="${list[i - 1]?.id}"]`) as HTMLInputElement | null
                )?.focus();
              } else if (e.key === "Tab") {
                e.preventDefault();
                update(i, { indent: Math.max(0, Math.min(3, it.indent + (e.shiftKey ? -1 : 1))) });
              }
            }}
            className={`flex-1 bg-transparent text-[13px] outline-none placeholder:text-dim ${it.done ? "text-dim line-through" : ""}`}
          />
          {it.done && canEdit ? (
            <button
              type="button"
              onClick={() => remove(i)}
              onPointerDown={(e) => e.stopPropagation()}
              className="text-dim hover:text-error"
              aria-label="×"
            >
              ×
            </button>
          ) : null}
        </div>
      ))}
      {list.length === 0 && canEdit ? (
        <button
          type="button"
          onClick={() => add(-1)}
          onPointerDown={(e) => e.stopPropagation()}
          className="py-1 text-[13px] text-dim hover:text-text"
        >
          + {t.todoItem}
        </button>
      ) : null}
    </div>
  );
}
